import React, { useState } from 'react';
import { Alert, Button, Form, Spinner } from 'react-bootstrap';
import {
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';

/**
 * Confirm SetupIntent or PaymentIntent with Payment Element.
 * @param {'setup'|'payment'} mode
 */
const StripePaymentForm = ({
  mode = 'payment',
  submitLabel = 'Pay',
  showSaveOptions = false,
  savePaymentMethod = true,
  onSavePaymentMethodChange,
  setAsDefault = true,
  onSetAsDefaultChange,
  onSuccess,
  onError,
  disabled = false,
  returnUrl,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!stripe || !elements || submitting || disabled) return;

    setSubmitting(true);
    setFormError('');

    try {
      const { error: submitError } = await elements.submit();
      if (submitError) {
        throw new Error(submitError.message);
      }

      let confirmError;
      let setupIntentId;
      let paymentIntentId;
      const redirectReturnUrl = returnUrl || window.location.href;
      if (mode === 'setup') {
        const result = await stripe.confirmSetup({
          elements,
          confirmParams: {
            return_url: redirectReturnUrl,
          },
          redirect: 'if_required',
        });
        confirmError = result.error;
        setupIntentId = result.setupIntent?.id;
      } else {
        const result = await stripe.confirmPayment({
          elements,
          confirmParams: {
            return_url: redirectReturnUrl,
          },
          redirect: 'if_required',
        });
        confirmError = result.error;
        paymentIntentId = result.paymentIntent?.id;
      }

      if (confirmError) {
        throw new Error(confirmError.message);
      }

      onSuccess?.({ setupIntentId, paymentIntentId });
    } catch (err) {
      const message = err?.message || 'Payment could not be completed.';
      setFormError(message);
      onError?.(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement options={{ layout: 'tabs' }} />

      {showSaveOptions && (
        <div className="mt-3">
          <Form.Check
            type="checkbox"
            id="save-payment-method"
            label="Save this card for future payments"
            checked={savePaymentMethod}
            onChange={(e) => onSavePaymentMethodChange?.(e.target.checked)}
          />
          {savePaymentMethod && (
            <Form.Check
              type="checkbox"
              id="set-default-payment-method"
              className="mt-2"
              label="Set as default payment method"
              checked={setAsDefault}
              onChange={(e) => onSetAsDefaultChange?.(e.target.checked)}
            />
          )}
        </div>
      )}

      {formError && (
        <Alert variant="danger" className="mt-3 mb-0 small">
          {formError}
        </Alert>
      )}

      <Button
        type="submit"
        variant="primary"
        className="mt-3 w-100"
        disabled={!stripe || !elements || submitting || disabled}
      >
        {submitting ? (
          <>
            <Spinner animation="border" size="sm" className="me-2" />
            Processing…
          </>
        ) : (
          submitLabel
        )}
      </Button>
    </form>
  );
};

export default StripePaymentForm;
